/**
 * VirtualizedDataTable — typed, virtualized table primitive backed by
 * TanStack Table v8 + TanStack Virtual v3.
 *
 * Built so the CSK proof-view tables (`CrossmapProofView`,
 * `CweNeutralizationProofView`) and any future registry surface can
 * render thousands of rows without jank. The semantic markup remains
 * a real `<table>` so screen readers and CSV-export tooling still see
 * row / cell structure; only the rendered subset of `<tr>` elements
 * changes between scroll positions.
 *
 * Highlights:
 *   - Headless typing through `ColumnDef<TData>` keeps the column
 *     contract co-located with the row data type.
 *   - The virtualizer renders a window of `<tr>` elements absolutely
 *     positioned inside a sized `<tbody>`. Header cells are sized via
 *     the same `getSize()` returned by TanStack Table so the column
 *     widths stay aligned across header and body.
 *   - When the row count is below {@link DEFAULT_VIRTUALIZE_THRESHOLD}
 *     the component falls back to a non-virtualized render. Below that
 *     threshold the virtualization overhead — measure callbacks,
 *     translate transforms, scroll-shadow recomputation — is bigger
 *     than the rendering cost it removes, and the resulting `<tbody>`
 *     is also easier to print and easier to inspect in devtools.
 *   - Tokens flow through the existing CSK design system; this
 *     primitive does not introduce any literal hex values.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import {
  flexRender,
  getCoreRowModel,
  type ColumnDef,
  type Row,
  useReactTable,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import React, { useMemo, useRef } from "react";
import { GRAY } from "../../design-system/tokens.js";

/** Number of rows above which the table switches to virtualized mode. */
export const DEFAULT_VIRTUALIZE_THRESHOLD = 80;

/** Estimated row height in pixels — used as the virtualizer's seed. */
export const DEFAULT_ROW_HEIGHT = 56;

export interface VirtualizedDataTableProps<TData> {
  /** Row data. */
  readonly data: readonly TData[];
  /** Typed column definitions. */
  readonly columns: readonly ColumnDef<TData>[];
  /** Stable identity per row — improves React reconciliation. */
  readonly getRowId?: (row: TData, index: number) => string;
  /**
   * Estimated row height. Used as the virtualizer's seed; the actual
   * height of each row is measured on mount via `ResizeObserver` so
   * variable-height rows still scroll correctly.
   */
  readonly estimateRowHeight?: number;
  /** Maximum scroll-area height. Defaults to `30rem`. */
  readonly maxHeight?: string;
  /** Minimum logical width of the table — propagated to `min-width`. */
  readonly minWidth?: string;
  /** Accessible label exposed on the scrolling region. */
  readonly ariaLabel: string;
  /**
   * Number of rows above which to virtualize. Defaults to
   * {@link DEFAULT_VIRTUALIZE_THRESHOLD}. Pass `0` to force
   * virtualization always; pass `Infinity` to never virtualize.
   */
  readonly virtualizeThreshold?: number;
  /**
   * Optional `data-testid` propagated to the scroll container so tests
   * can target the virtualized region directly.
   */
  readonly testId?: string;
  /** Rendered when {@link data} is empty. */
  readonly empty?: React.ReactNode;
}

const TABLE_BASE_STYLE: React.CSSProperties = {
  borderCollapse: "collapse",
  width: "100%",
};

const HEADER_CELL_STYLE: React.CSSProperties = {
  borderBottom: `1px solid ${GRAY[300]}`,
  color: GRAY[600],
  fontSize: "0.75rem",
  fontWeight: 700,
  letterSpacing: "0.04em",
  padding: "0.625rem 0.75rem",
  textAlign: "left",
  textTransform: "uppercase",
  verticalAlign: "top",
};

const BODY_CELL_STYLE: React.CSSProperties = {
  borderBottom: `1px solid ${GRAY[300]}`,
  fontSize: "0.875rem",
  padding: "0.625rem 0.75rem",
  verticalAlign: "top",
};

interface VirtualBodyProps<TData> {
  readonly rows: readonly Row<TData>[];
  readonly estimateRowHeight: number;
  readonly columnCount: number;
  readonly scrollRef: React.RefObject<HTMLDivElement | null>;
}

/**
 * Body renderer for the virtualized branch. Lives in its own component
 * so {@link useVirtualizer} only mounts when the consumer actually
 * exceeds the virtualization threshold. The hook installs scroll +
 * ResizeObserver listeners on its scroll element; pulling it into a
 * conditional component avoids paying that cost on small tables.
 */
function VirtualBody<TData>({
  rows,
  estimateRowHeight,
  columnCount,
  scrollRef,
}: VirtualBodyProps<TData>): React.JSX.Element {
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => estimateRowHeight,
    overscan: 8,
  });

  const virtualRows = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();
  const paddingTop = virtualRows.length > 0 ? (virtualRows[0]?.start ?? 0) : 0;
  const paddingBottom =
    virtualRows.length > 0 ? totalSize - (virtualRows[virtualRows.length - 1]?.end ?? 0) : 0;

  return (
    <tbody data-csk-table-body="virtualized">
      {paddingTop > 0 && (
        <tr aria-hidden="true">
          <td colSpan={columnCount} style={{ height: paddingTop, padding: 0, border: 0 }} />
        </tr>
      )}
      {virtualRows.map((virtualRow) => {
        const row = rows[virtualRow.index];
        if (row === undefined) return null;
        return (
          <tr
            key={row.id}
            ref={(node): void => {
              if (node !== null) virtualizer.measureElement(node);
            }}
            data-index={virtualRow.index}
          >
            {row.getVisibleCells().map((cell) => (
              <td key={cell.id} style={BODY_CELL_STYLE}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            ))}
          </tr>
        );
      })}
      {paddingBottom > 0 && (
        <tr aria-hidden="true">
          <td colSpan={columnCount} style={{ height: paddingBottom, padding: 0, border: 0 }} />
        </tr>
      )}
    </tbody>
  );
}

/**
 * Render a typed table with optional row virtualization.
 *
 * The component switches between a virtualized and a non-virtualized
 * `<tbody>` depending on row count. Both branches honor the same
 * column defs and the same cell styles, so consumers do not have to
 * branch on the row-count expectation.
 */
function VirtualizedDataTableInner<TData>({
  data,
  columns,
  getRowId,
  estimateRowHeight = DEFAULT_ROW_HEIGHT,
  maxHeight = "30rem",
  minWidth,
  ariaLabel,
  virtualizeThreshold = DEFAULT_VIRTUALIZE_THRESHOLD,
  testId,
  empty,
}: VirtualizedDataTableProps<TData>): React.JSX.Element {
  const scrollRef = useRef<HTMLDivElement>(null);

  const table = useReactTable({
    data: data as TData[],
    columns: columns as ColumnDef<TData>[],
    getCoreRowModel: getCoreRowModel(),
    ...(getRowId !== undefined ? { getRowId } : {}),
  });

  const rows = table.getRowModel().rows;

  const tableStyle = useMemo<React.CSSProperties>(
    () => (minWidth !== undefined ? { ...TABLE_BASE_STYLE, minWidth } : { ...TABLE_BASE_STYLE }),
    [minWidth],
  );

  if (rows.length === 0 && empty !== undefined) {
    return <>{empty}</>;
  }

  const shouldVirtualize = rows.length >= virtualizeThreshold;
  const columnCount = table.getAllLeafColumns().length;

  return (
    <div
      ref={scrollRef}
      data-csk-table="virtualized"
      data-csk-table-virtualized={shouldVirtualize ? "true" : "false"}
      data-testid={testId}
      role="region"
      aria-label={ariaLabel}
      tabIndex={0}
      style={{
        contain: "strict",
        maxHeight: shouldVirtualize ? maxHeight : undefined,
        overflow: "auto",
      }}
    >
      <table style={tableStyle}>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th key={header.id} scope="col" style={HEADER_CELL_STYLE}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        {shouldVirtualize ? (
          <VirtualBody
            rows={rows}
            estimateRowHeight={estimateRowHeight}
            columnCount={columnCount}
            scrollRef={scrollRef}
          />
        ) : (
          <tbody data-csk-table-body="static">
            {rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} style={BODY_CELL_STYLE}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        )}
      </table>
    </div>
  );
}

/**
 * Memoized VirtualizedDataTable export. The cast preserves the
 * generic parameter that `React.memo` would otherwise erase.
 */
export const VirtualizedDataTable = React.memo(
  VirtualizedDataTableInner,
) as typeof VirtualizedDataTableInner;
